import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { updatePassword, supabase } from '../utils/supabase'

const ResetPassword = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Listen for password recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event)
      
      if (event === "PASSWORD_RECOVERY") {
        console.log('Password recovery detected')
        setIsPasswordRecovery(true)
        setMessage('Please enter your new password below.')
      }
    })

    // Check if we're already in a password recovery session
    const checkRecoveryState = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session && session.user) {
        // Check URL parameters for recovery indicators
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('type') === 'recovery' || urlParams.get('type') === 'password_recovery') {
          setIsPasswordRecovery(true)
          setMessage('Please enter your new password below.')
        }
      }
    }

    checkRecoveryState()

    return () => subscription.unsubscribe()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validation
    if (!password) {
      setError('Please enter a new password')
      setLoading(false)
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      setLoading(false)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (!isPasswordRecovery) {
      setError('Invalid password reset session. Please request a new reset link.')
      setLoading(false)
      return
    }

    try {
      const { error } = await updatePassword(password)
      
      if (error) {
        throw error
      }

      setMessage('Password updated successfully! Redirecting to login...')
      
      // Sign out the user to ensure they log in with new password
      await supabase.auth.signOut()
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/')
      }, 3000)
      
    } catch (error) {
      console.error('Password update error:', error)
      if (error.message.includes('session_not_found')) {
        setError('Your password reset session has expired. Please request a new reset link.')
      } else {
        setError('Failed to update password: ' + error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // If not in password recovery mode, show instructions
  if (!isPasswordRecovery) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>Task Manager</h1>
          <h2>Reset Password</h2>
          
          <div className="info-box" style={{ padding: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px' }}>
            <p style={{ margin: '0 0 12px 0' }}><strong>Invalid Access</strong></p>
            <p style={{ margin: '0 0 12px 0' }}>
              This page can only be accessed through a password reset link sent to your email.
            </p>
            <p style={{ margin: 0 }}>
              If you need to reset your password, please request a new reset link.
            </p>
          </div>
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Link to="/forgot-password" className="btn btn-primary" style={{ textDecoration: 'none', marginRight: '12px' }}>
              Request Password Reset
            </Link>
            <Link to="/" className="link-button">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Task Manager</h1>
        <h2>Set New Password</h2>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your new password"
              required
              minLength={8}
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              minLength={8}
              disabled={loading}
            />
          </div>
          
          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Updating Password...' : 'Update Password'}
          </button>
        </form>
        
        <div className="info-box" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '6px', fontSize: '14px' }}>
          <p style={{ margin: '0 0 8px 0' }}><strong>Password Requirements:</strong></p>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>At least 8 characters long</li>
            <li>Both passwords must match</li>
            <li>You'll be signed out after updating (for security)</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword